import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { buildGraphQLFields } from 'src/app/helpers/datesFormat';
import { OpIncident } from 'src/app/incidencias/incidents.types';
import { GraphService } from './graph.service';

interface FindAllOpIncidentsPage {
  opIncidents: OpIncident[];
  lastEvaluatedKeyId?: string | null;
}

interface FindAllPreOperationalIncidentsPage {
  preOpIncidents: Array<{
    id: string;
    itineraryId: string;
    type: string;
    destination: string;
    incidentReason: string;
    description: string;
    deadline: string;
    incidentDate: string;
    incidentStatus: string;
    approvedBy?: string;
    createdAt: string;
    updatedAt: string;
    historial: Array<{
      timestamp: string;
      updatedBy: string;
      incidentStatus: string;
    }>;
  }>;
  lastEvaluatedKeyId?: string | null;
}

interface FindAllRevisionACPage {
  csReviews: Array<{
    id: string;
    itineraryId: string;
    destination: string;
    activityName: string;
    activityDay: number;
    description: string;
    incidentDate: string;
    incidentStatus: string;
    historial: Array<{
      timestamp: string;
      updatedBy: string;
      incidentStatus: string;
    }>;
  }>;
  lastEvaluatedKeyId?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class IncidentService {
  constructor(private readonly apiService: GraphService) {}
  createOpIncident({
    itineraryId,
    serviceId = null,
    opIncidentType,
    incidentStatus,
    description,

    reportedBy,

    actions = null,
    solutionDate = null,
    solutionTime = null
  }: {
    itineraryId: string;
    serviceId?: string | null;

    opIncidentType: string;
    incidentStatus: string;
    description: string;
    reportedBy: string;

    actions?: string | null;
    solutionDate?: string | null;
    solutionTime?: string | null;
  }) {
    const creationBody: any = {
      itineraryId,
      opIncidentType,
      incidentStatus,
      description,
      reportedBy
    };

    if (serviceId) {
      creationBody['serviceId'] = serviceId;
    }

    if (actions) {
      creationBody['actions'] = actions;
    }

    if (solutionDate) {
      creationBody['solutionDate'] = solutionDate;
    }

    if (solutionTime) {
      creationBody['solutionTime'] = solutionTime;
    }

    console.log({
      creationBody
    });

    return this.apiService.mutateGraphQLSales<{
      createOpIncident: {
        id: string;
      };
    }>({
      mutation: `
        mutation CreateOpIncident (
          $op_incident_create: OpIncidentCreationDto!
        ) {
          createOpIncident(
            op_incident_create: $op_incident_create
          )
        }`,
      variables: {
        op_incident_create: creationBody
      }
    });
  }

  findAllOpIncidents({
    selectedFields,
    startEvaluatedKeyId
  }: {
    selectedFields?: string[];
    startEvaluatedKeyId?: string | null;
  } = {}) {
    const projectionExpression = selectedFields?.length
      ? buildGraphQLFields(selectedFields)
      : `id,type`;

    const pagination =
      startEvaluatedKeyId == null ? undefined : { startEvaluatedKeyId };

    return this.apiService.queryGraphQLSales<{
      findAllOpIncidents: FindAllOpIncidentsPage;
    }>({
      query: `query FindAllOpIncidents (
        $selectedFields: [String!]
        $pagination: IncidenciasPaginationInput
      ) {
          findAllOpIncidents (
            selectedFields: $selectedFields,
            pagination: $pagination
          ) {
              lastEvaluatedKeyId,
              opIncidents {
                ${projectionExpression}
              }
          }
        }`,
      variables: {
        selectedFields,
        pagination
      }
    });
  }

  findOpIncidentsByServiceId({
    itineraryId,
    serviceId
  }: {
    itineraryId: string;
    serviceId: string;
  }) {
    return this.apiService.queryGraphQLSales<{
      findOpIncidentsByServiceId: OpIncident[];
    }>({
      query: `query FindOpIncidentsByServiceId (
          $itineraryId: String!
        ) {
          findOpIncidentsByServiceId(
            itineraryId: $itineraryId
          ) {
              id,
              itineraryId,
              serviceId,

              opIncidentType,
              incidentStatus,
              description,
              reportedBy,

              actions,
              solutionDate,
              solutionTime
            }
          }`,
      variables: { itineraryId }
    });
  }

  findOpIncidentsByItineraryId({ itineraryId }: { itineraryId: string }) {
    return this.apiService.queryGraphQLSales<{
      findOpIncidentsByItineraryId: OpIncident[];
    }>({
      query: `query FindOpIncidentsByItineraryId (
          $itineraryId: String!
        ) {
          findOpIncidentsByItineraryId(
            itineraryId: $itineraryId
          ) {
              id,
              itineraryId,
              serviceId,

              opIncidentType,
              incidentStatus,
              description,
              reportedBy,

              actions,
              solutionDate,
              solutionTime,

              createdAt
            }
          }`,
      variables: { itineraryId }
    });
  }

  updateOpIncident({
    incidentId,
    serviceId = null,
    itineraryId,
    modifiedBy,

    incidentStatus,
    opIncidentType,
    description,

    solutionDate,
    solutionTime,
    actions
  }: {
    incidentId: string;
    itineraryId: string;

    serviceId: string | null;

    modifiedBy: string;

    opIncidentType?: string;
    incidentStatus?: string;
    description?: string;

    solutionDate?: string;
    solutionTime?: string;
    actions?: string;
  }) {
    const op_incident_update: any = {
      id: incidentId,
      itineraryId,
      modifiedBy,
      serviceId
    };

    if (incidentStatus) {
      op_incident_update['incidentStatus'] = incidentStatus;
    }
    if (opIncidentType) {
      op_incident_update['opIncidentType'] = opIncidentType;
    }
    if (description) {
      op_incident_update['description'] = description;
    }
    if (solutionDate) {
      op_incident_update['solutionDate'] = solutionDate;
    }
    if (solutionTime) {
      op_incident_update['solutionTime'] = solutionTime;
    }
    if (actions) {
      op_incident_update['actions'] = actions;
    }

    return this.apiService.mutateGraphQLSales({
      mutation: `
        mutation UpdateOpIncident (
          $op_incident_update: UpdateOpIncidentResolverDto!
        ) {
          updateOpIncident(
            op_incident_update: $op_incident_update
          )
        }`,
      variables: {
        op_incident_update
      }
    });
  }

  deleteOpIncident({
    opIncidentId,
    itineraryId,
    serviceId = undefined,
    deletedBy
  }: {
    opIncidentId: string;
    itineraryId: string;
    serviceId?: string;
    deletedBy: string;
  }) {
    const op_incident_delete: any = {
      opIncidentId,
      itineraryId,
      deletedBy
    };

    if (serviceId) {
      op_incident_delete['serviceId'] = serviceId;
    }

    return this.apiService.mutateGraphQLSales({
      mutation: `
        mutation DeleteOpIncident (
          $op_incident_delete: DeleteOpIncidentInputDto!
        ) {
          deleteOpIncident(
            op_incident_delete: $op_incident_delete
          )
        }`,
      variables: {
        op_incident_delete
      }
    });
  }

  createPreOpIncident({
    itineraryId,
    destination,
    incidentReason,
    description,
    deadline,
    incidentDate,
    createdBy
  }: {
    itineraryId: string;
    destination: string;
    incidentReason: string;
    description: string;
    deadline: string;
    incidentDate: string;
    createdBy: string;
  }) {
    return this.apiService.mutateGraphQLSales({
      mutation: `
        mutation CreateIncidenciaPreOperativa (
          $incidencia_pre_operativa_create: CreateIncidenciaPreOperativaResolverDto!
        ) {
          createIncidenciaPreOperativa(
            incidencia_pre_operativa_create: $incidencia_pre_operativa_create
          )
        }`,
      variables: {
        incidencia_pre_operativa_create: {
          itineraryId,
          destination,
          incidentReason,
          description,
          deadline,
          incidentDate,
          createdBy
        }
      }
    });
  }

  findAllPreOperationalIncidents({
    startEvaluatedKeyId
  }: {
    startEvaluatedKeyId?: string | null;
  } = {}) {
    const pagination =
      startEvaluatedKeyId == null ? undefined : { startEvaluatedKeyId };

    return this.apiService.queryGraphQLSales<{
      findAllIncidenciasPreOperativas: FindAllPreOperationalIncidentsPage;
    }>({
      query: `query FindAllIncidenciasPreOperativas (
        $pagination: IncidenciasPaginationInput
      ) {
        findAllIncidenciasPreOperativas(
          pagination: $pagination
        ) {
          lastEvaluatedKeyId
          preOpIncidents {
            id
            itineraryId
            type
            destination
            incidentReason
            description
            deadline
            incidentDate
            incidentStatus
            approvedBy
            createdAt
            updatedAt
            historial {
              timestamp
              updatedBy
              incidentStatus
            }
          }
        }
      }`,
      variables: {
        pagination
      }
    });
  }

  findPreOpIncidentsByItineraryId({ itineraryId }: { itineraryId: string }) {
    return this.apiService.queryGraphQLSales<{
      findIncidenciasPreOperativasByItineraryId: Array<{
        id: string;
        itineraryId: string;
        type: string;
        destination: string;
        incidentReason: string;
        description: string;
        deadline: string;
        incidentDate: string;
        incidentStatus: string;
        createdAt: string;
        updatedAt: string;
        deletedAt: string;
      }>;
    }>({
      query: `query FindIncidenciasPreOperativasByItineraryId (
          $itineraryId: String!
        ) {
          findIncidenciasPreOperativasByItineraryId(
            itineraryId: $itineraryId
          ) {
              id,
              itineraryId,
              type,
              destination,
              incidentReason,
              description,
              deadline,
              incidentDate,
              incidentStatus,
              approvedBy
            }
          }`,
      variables: { itineraryId }
    });
  }

  updatePreOpIncident({
    incidentId,
    itineraryId,
    incidentStatus,
    modifiedBy
  }: {
    incidentId: string;
    itineraryId: string;
    incidentStatus: string;
    modifiedBy: string;
  }) {
    return this.apiService.mutateGraphQLSales({
      mutation: `
        mutation UpdateIncidenciaPreOperativa (
          $incidencia_pre_operativa_update: UpdateIncidenciaPreOperativaResolverDto!
        ) {
          updateIncidenciaPreOperativa(
            incidencia_pre_operativa_update: $incidencia_pre_operativa_update
          )
        }`,
      variables: {
        incidencia_pre_operativa_update: {
          id: incidentId,
          itineraryId,
          incidentStatus,
          modifiedBy
        }
      }
    });
  }

  deletePreOpIncident({
    preOpIncidentId,
    itineraryId,
    deletedBy
  }: {
    preOpIncidentId: string;
    itineraryId: string;
    deletedBy: string;
  }) {
    return this.apiService.mutateGraphQLSales({
      mutation: `
        mutation DeleteIncidenciaPreOperativa (
          $incidencia_pre_operativa_delete: DeleteIncidenciaPreOperativaResolverDto!
        ) {
          deleteIncidenciaPreOperativa(
            incidencia_pre_operativa_delete: $incidencia_pre_operativa_delete
          )
        }`,
      variables: {
        incidencia_pre_operativa_delete: {
          preOpIncidentId,
          itineraryId,
          deletedBy
        }
      }
    });
  }

  createCSReview({
    activityId,
    itineraryId,
    destination,
    activityName,
    activityDay,
    description,
    incidentDate,
    incidentStatus,
    createdBy
  }: {
    activityId: string;
    itineraryId: string;
    description: string;
    incidentDate: string;
    destination: string;
    activityName: string;
    activityDay: number;
    incidentStatus: 'PENDING' | 'APPROVED';
    createdBy: string;
  }): Observable<{
    createRevisionAC: string;
  }> {
    return this.apiService.mutateGraphQLSales({
      mutation: `
        mutation CreateRevisionAC (
          $revision_ac_create: CreateRevisionACResolverDto!
        ) {
          createRevisionAC(
            revision_ac_create: $revision_ac_create
          )
        }`,
      variables: {
        revision_ac_create: {
          activityId,
          itineraryId,
          description,
          incidentDate,
          incidentStatus,
          destination,
          activityName,
          activityDay,
          createdBy
        }
      }
    });
  }

  findAllCsReviews({
    startEvaluatedKeyId
  }: {
    startEvaluatedKeyId?: string | null;
  } = {}) {
    const pagination =
      startEvaluatedKeyId == null ? undefined : { startEvaluatedKeyId };

    return this.apiService.queryGraphQLSales<{
      findAllRevisionAC: FindAllRevisionACPage;
    }>({
      query: `query FindAllRevisionAC (
        $pagination: IncidenciasPaginationInput
      ) {
        findAllRevisionAC(
          pagination: $pagination
        ) {
          lastEvaluatedKeyId
          csReviews {
            id
            itineraryId
            destination
            activityName
            activityDay
            description
            incidentDate
            incidentStatus
            historial {
              timestamp
              updatedBy
              incidentStatus
            }
          }
        }
      }`,
      variables: {
        pagination
      }
    });
  }

  findCsReviewByActivityId({
    itineraryId,
    activityId
  }: {
    itineraryId: string;
    activityId: string;
  }) {
    return this.apiService.queryGraphQLSales<{
      findRevisionACByActivityId: Array<{
        id: string;
        activityId: string;
        itineraryId: string;
        type: string;

        destination: string;
        activityName: string;
        activityDay: number;

        description: string;
        incidentDate: string;
        incidentStatus: string;

        createdAt: string;
        updatedAt: string;
      }>;
    }>({
      query: `query FindRevisionACByActivityId (
          $itineraryId: String!,
          $activityId: String!
        ) {
          findRevisionACByActivityId(
            itineraryId: $itineraryId,
            activityId: $activityId
          ) {
              id,
              itineraryId,
              activityId,
              type,

              destination,
              activityName,
              activityDay,

              description,
              incidentDate,
              incidentStatus,

              approvedBy,
              notApprovedBy,
              resolvedBy
            }
          }`,
      variables: { itineraryId, activityId }
    });
  }

  findCSReviewByItineraryId({ itineraryId }: { itineraryId: string }) {
    return this.apiService.queryGraphQLSales<{
      findRevisionACByItineraryId: Array<{
        id: string;
        activityId: string;
        itineraryId: string;

        destination: string;
        activityDay: number;
        activityName: string;

        description: string;
        incidentDate: string;
        incidentStatus: string;

        resolvedBy: string;
        approvedBy: string;
        notApprovedBy: string;

        createdAt: string;
        updatedAt: string;
      }>;
    }>({
      query: `query FindRevisionACByItineraryId (
          $itineraryId: String!
        ) {
          findRevisionACByItineraryId(
            itineraryId: $itineraryId
          ) {
              id,
              activityId,
              itineraryId,

              destination,
              activityDay,
              activityName,

              description,
              incidentDate,
              incidentStatus,

              resolvedBy,
              notApprovedBy,
              approvedBy,

              createdAt,
              updatedAt
          }
        }`,
      variables: { itineraryId }
    });
  }

  updateCSReview({
    incidentId,
    activityId,
    itineraryId,
    incidentStatus,
    description,
    modifiedBy
  }: {
    incidentId: string;
    activityId: string;
    itineraryId: string;
    incidentStatus: string;
    description?: string;
    modifiedBy: string;
  }) {
    const revisionAcUpdate: any = {
      id: incidentId,
      activityId,
      itineraryId,
      incidentStatus,
      modifiedBy
    };

    if (description !== undefined) {
      revisionAcUpdate['description'] = description;
    }

    console.log({ revisionAcUpdate });

    return this.apiService.mutateGraphQLSales({
      mutation: `
        mutation UpdateRevisionAC (
          $revision_ac_update: UpdateRevisionACResolverDto!
        ) {
          updateRevisionAC(
            revision_ac_update: $revision_ac_update
          )
        }`,
      variables: {
        revision_ac_update: revisionAcUpdate
      }
    });
  }

  deleteCSReview({
    csReviewId,
    itineraryId,
    activityId,
    deletedBy
  }: {
    csReviewId: string;
    itineraryId: string;
    activityId: string;
    deletedBy: string;
  }) {
    return this.apiService.mutateGraphQLSales({
      mutation: `
        mutation DeleteRevisionAC (
          $revision_ac_delete: DeleteRevisionACResolverDto!
        ) {
          deleteRevisionAC(
            revision_ac_delete: $revision_ac_delete
          )
        }`,
      variables: {
        revision_ac_delete: {
          csReviewId,
          itineraryId,
          activityId,
          deletedBy
        }
      }
    });
  }
}
